import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import useAyPhaseReady from '../../../utils/useAyPhaseReady';
import { usePhase } from '../../../contexts/PhaseContext';

const nurseryEmojiOptions = [
  { value: 1, emoji: '😢', label: 'experiencing significant difficulty', bg: '#ffebee', bd: '#e53935', text: '#b71c1c' },
  { value: 2, emoji: '😕', label: 'experiencing some difficulty', bg: '#fff3e0', bd: '#fb8c00', text: '#e65100' },
  { value: 3, emoji: '🙂', label: 'managing comfortably', bg: '#fffde7', bd: '#fdd835', text: '#f57f17' },
  { value: 4, emoji: '😃', label: 'capable and competent', bg: '#f1f8e9', bd: '#7cb342', text: '#33691e' },
  { value: 5, emoji: '🌟', label: 'highly capable and competent', bg: '#e8f5e9', bd: '#43a047', text: '#1b5e20' }
];

const GradingScalePage = () => {
  const { t } = useTranslation();
  const { activePhase } = usePhase();
  const [gradingScales, setGradingScales] = useState([]);
  const [classLevel, setClassLevel] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentScale, setCurrentScale] = useState(null);
  const [formData, setFormData] = useState({
    remarkCode: '',
    grade: '',
    description: '',
    emojiValue: 1,
    minScore: 0,
    maxScore: 0,
    gradePoint: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { ready: ayPhaseReady } = useAyPhaseReady();

  const fetchGradingScales = async () => {
    try {
      setLoading(true);
      setError(null);
      const qs = new URLSearchParams();
      if (classLevel) qs.set('classLevel', classLevel);
      const phaseCode = String(activePhase || '').toLowerCase();
      if (phaseCode === 'sec' && !classLevel) {
        setGradingScales([]);
        setLoading(false);
        return;
      }
      if (phaseCode === 'sec' && (classLevel === 'first_cycle' || classLevel === 'second_cycle')) {
        qs.set('includeFallback', '1');
      }
      const isNurseryEmojiMode = phaseCode === 'nur_pri' && classLevel === 'nursery';
      if (isNurseryEmojiMode) qs.set('scaleType', 'emoji');
      const data = await fetchJson(`/api/academics/grade-scales${qs.toString() ? `?${qs.toString()}` : ''}`);
      const list = Array.isArray(data)
        ? data
        : (Array.isArray(data?.gradeScales) ? data.gradeScales : (data?.data || data?.scales || []));
      setGradingScales(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to fetch grading scales:', error);
      setError(error?.message || t('academics.gradingScale.errors.fetch'));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (ayPhaseReady) {
      fetchGradingScales();
    }
  }, [ayPhaseReady, classLevel, activePhase]);

  useEffect(() => {
    const phaseCode = String(activePhase || '').toLowerCase();
    if (phaseCode === 'nur_pri' && !classLevel) {
      setClassLevel('primary');
    }
    if (phaseCode === 'nur_pri' && classLevel && classLevel !== 'primary' && classLevel !== 'nursery') {
      setClassLevel('primary');
    }
    if (phaseCode === 'sec' && !classLevel) {
      setClassLevel('first_cycle');
    }
    if (phaseCode === 'sec' && classLevel && classLevel !== 'first_cycle' && classLevel !== 'second_cycle') {
      setClassLevel('first_cycle');
    }
    if (phaseCode !== 'nur_pri' && phaseCode !== 'sec' && classLevel) {
      setClassLevel('');
    }
  }, [activePhase]);

  const handleOpenDialog = (scale = null) => {
    const phaseCode = String(activePhase || '').toLowerCase();
    const isNurseryEmojiMode = phaseCode === 'nur_pri' && classLevel === 'nursery';
    setCurrentScale(scale);
    if (scale) {
      setFormData({
        remarkCode: scale.remarkCode || scale.grade || scale.name || '',
        grade: scale.grade || '',
        description: scale.description || '',
        emojiValue: scale.emojiValue ?? scale.minScore ?? 1,
        minScore: scale.minScore ?? 0,
        maxScore: scale.maxScore ?? 0,
        gradePoint: scale.gradePoint ?? 0
      });
    } else {
      setFormData({
        remarkCode: '',
        grade: '',
        description: isNurseryEmojiMode ? 'experiencing significant difficulty' : '',
        emojiValue: 1,
        minScore: 0,
        maxScore: 0,
        gradePoint: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentScale(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      const phaseCode = String(activePhase || '').toLowerCase();
      const isNurseryEmojiMode = phaseCode === 'nur_pri' && classLevel === 'nursery';

      const payload = isNurseryEmojiMode
        ? {
            scaleType: 'emoji',
            emojiValue: Number(formData.emojiValue),
            description: formData.description,
            ...(classLevel ? { classLevel } : {})
          }
        : {
            name: formData.remarkCode || formData.grade,
            grade: formData.grade || formData.remarkCode,
            remarkCode: formData.remarkCode || formData.grade,
            description: formData.description,
            minScore: Number(formData.minScore),
            maxScore: Number(formData.maxScore),
            gradePoint: Number(formData.gradePoint),
            ...(classLevel ? { classLevel } : {})
          };

      const url = currentScale
        ? `/api/academics/grade-scales/${currentScale.id}`
        : '/api/academics/grade-scales';
      const method = currentScale ? 'PUT' : 'POST';

      setSaving(true);
      await fetchJson(url, {
        method,
        body: JSON.stringify(payload)
      });

      await fetchGradingScales();
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save grading scale:', error);
      setError(error?.message || t('academics.gradingScale.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetchJson(`/api/academics/grade-scales/${id}`, {
        method: 'DELETE'
      });
      fetchGradingScales();
    } catch (error) {
      console.error('Failed to delete grading scale:', error);
      setError(error?.message || t('academics.gradingScale.errors.delete'));
    }
  };

  const hasData = gradingScales.length > 0;
  const phaseCode = String(activePhase || '').toLowerCase();
  const isNurseryEmojiMode = phaseCode === 'nur_pri' && classLevel === 'nursery';
  const actionColumns = hasData ? 1 : 0;
  const tableColumns = (isNurseryEmojiMode ? 3 : 6) + actionColumns;

  const nurseryStyleForLevel = (level) => {
    const n = Number(level);
    const hit = nurseryEmojiOptions.find((o) => Number(o.value) === n) || null;
    const reportCardPalette = {
      1: { fill: '#e63936', stroke: '#b30000' },
      2: { fill: '#fa8c00', stroke: '#c2226b' },
      3: { fill: '#fdd93b', stroke: '#e67300' },
      4: { fill: '#42b047', stroke: '#1c5e21' },
      5: { fill: '#ffcc00', stroke: '#d9a600' }
    };
    const palette = reportCardPalette[n] || { fill: '#f5f5f5', stroke: '#e0e0e0' };
    return {
      ...(hit || { value: n, emoji: '', label: '' }),
      bg: palette.fill,
      bd: palette.stroke,
      text: '#424242'
    };
  };

  const renderNurseryEmojiBadge = (meta, size = 34) => {
    const level = Number(meta?.value);
    const facial = '#0f172a';
    const stroke = meta?.bd || '#94a3b8';
    const fill = meta?.bg || '#f8fafc';
    const mouthPath =
      level >= 4
        ? 'M 30 62 Q 50 82 70 62'
        : level === 3
          ? 'M 30 64 Q 50 70 70 64'
          : level === 2
            ? 'M 30 68 Q 50 56 70 68'
            : 'M 30 72 Q 50 48 70 72';
    const starPoints = '50,10 61,36 90,39 68,57 75,86 50,70 25,86 32,57 10,39 39,36';

    return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        lineHeight: 1,
        mx: 'auto'
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 100 100"
        sx={{ width: size, height: size, display: 'block' }}
      >
        {level === 5 ? (
          <polygon points={starPoints} fill={fill} stroke={stroke} strokeWidth="6" strokeLinejoin="round" />
        ) : (
          <>
            <circle cx="50" cy="50" r="36" fill={fill} stroke={stroke} strokeWidth="6" />
            <circle cx="38" cy="42" r="5.5" fill={facial} />
            <circle cx="62" cy="42" r="5.5" fill={facial} />
            <circle cx="40" cy="39.5" r="1.4" fill="#ffffff" />
            <circle cx="64" cy="39.5" r="1.4" fill="#ffffff" />
            {level === 1 && (
              <>
                <line x1="25" y1="31" x2="43" y2="37" stroke={facial} strokeWidth="4" strokeLinecap="round" />
                <line x1="57" y1="37" x2="75" y2="31" stroke={facial} strokeWidth="4" strokeLinecap="round" />
                <path d="M 34 50 C 31 55, 31 60, 35 64 C 40 60, 40 54, 34 50 Z" fill="#6cb6ff" stroke="#3b82f6" strokeWidth="2" />
              </>
            )}
            <path d={mouthPath} fill="none" stroke={facial} strokeWidth="5" strokeLinecap="round" />
          </>
        )}
      </Box>
    </Box>
    );
  };

  return (
    <Container sx={{ pt: 2 }}>
      <Typography variant="h4" gutterBottom>
        {t('academics.gradingScale.title')}
      </Typography>

      {String(activePhase || '').toLowerCase() === 'nur_pri' && (
        <Tabs
          value={classLevel === 'nursery' ? 'nursery' : 'primary'}
          onChange={(_, next) => setClassLevel(next === 'nursery' ? 'nursery' : 'primary')}
          sx={{ mt: 2 }}
        >
          <Tab value="primary" label="Primary" />
          <Tab value="nursery" label="Nursery" />
        </Tabs>
      )}

      {String(activePhase || '').toLowerCase() === 'sec' && (
        <Tabs
          value={classLevel === 'second_cycle' ? 'second_cycle' : 'first_cycle'}
          onChange={(_, next) => setClassLevel(next === 'second_cycle' ? 'second_cycle' : 'first_cycle')}
          sx={{ mt: 2 }}
        >
          <Tab value="first_cycle" label="First Cycle" />
          <Tab value="second_cycle" label="Second Cycle" />
        </Tabs>
      )}
      
      <Button 
        variant="contained" 
        color="primary" 
        onClick={() => handleOpenDialog()}
      >
        {t('academics.gradingScale.actions.addNew')}
        {phaseCode === 'sec' ? (classLevel === 'second_cycle' ? ' (Second Cycle)' : ' (First Cycle)') : ''}
      </Button>
      
      {!ayPhaseReady && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">{t('academics.gradingScale.messages.preparingContext')}</Typography>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} style={{ marginTop: 20, opacity: ayPhaseReady ? 1 : 0.6 }}>
        <Table>
          <TableHead>
            {!isNurseryEmojiMode ? (
              <TableRow>
                <TableCell>{t('academics.gradingScale.table.headers.remarkCode')}</TableCell>
                <TableCell>{t('academics.gradingScale.table.headers.grade')}</TableCell>
                <TableCell>{t('academics.gradingScale.table.headers.description')}</TableCell>
                <TableCell>{t('academics.gradingScale.table.headers.minMark')}</TableCell>
                <TableCell>{t('academics.gradingScale.table.headers.maxMark')}</TableCell>
                <TableCell>{t('academics.gradingScale.table.headers.gradePoint')}</TableCell>
                {hasData && <TableCell>{t('academics.gradingScale.table.headers.actions')}</TableCell>}
              </TableRow>
            ) : (
              <TableRow>
                <TableCell align="center">Emoji</TableCell>
                <TableCell align="center">Level</TableCell>
                <TableCell>Description</TableCell>
                {hasData && <TableCell>{t('academics.gradingScale.table.headers.actions')}</TableCell>}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tableColumns} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : !hasData ? (
              <TableRow>
                <TableCell colSpan={tableColumns} align="center">
                  {t('academics.gradingScale.messages.empty')}
                </TableCell>
              </TableRow>
            ) : (
              gradingScales.map((scale) => (
                <TableRow key={scale.id}>
                  {!isNurseryEmojiMode ? (
                    <>
                      <TableCell>{scale.remarkCode || scale.grade}</TableCell>
                      <TableCell>{scale.grade}</TableCell>
                      <TableCell>{scale.description}</TableCell>
                      <TableCell>{scale.minScore}</TableCell>
                      <TableCell>{scale.maxScore}</TableCell>
                      <TableCell>{scale.gradePoint}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="center" sx={{ verticalAlign: 'middle', py: 1.5 }}>
                        {(() => {
                          const level = scale.emojiValue ?? scale.minScore ?? null;
                          const meta = nurseryStyleForLevel(level);
                          return renderNurseryEmojiBadge(meta);
                        })()}
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: 'middle', fontWeight: 600 }}>
                        {scale.emojiValue ?? scale.minScore ?? ''}
                      </TableCell>
                      <TableCell>{scale.description}</TableCell>
                    </>
                  )}
                  {hasData && (
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleOpenDialog(scale)}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleDelete(scale.id)}
                        sx={{ ml: 1 }}
                      >
                        {t('common.delete')}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {currentScale ? t('academics.gradingScale.dialog.editTitle') : t('academics.gradingScale.dialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          {!isNurseryEmojiMode ? (
            <>
              <TextField
                autoFocus
                margin="dense"
                name="remarkCode"
                label={t('academics.gradingScale.fields.remarkCode')}
                type="text"
                fullWidth
                value={formData.remarkCode}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="grade"
                label={t('academics.gradingScale.fields.gradeSymbol')}
                type="text"
                fullWidth
                value={formData.grade}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="description"
                label={t('academics.gradingScale.fields.description')}
                type="text"
                fullWidth
                value={formData.description}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="minScore"
                label={t('academics.gradingScale.fields.minScore')}
                type="number"
                fullWidth
                value={formData.minScore}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="maxScore"
                label={t('academics.gradingScale.fields.maxScore')}
                type="number"
                fullWidth
                value={formData.maxScore}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="gradePoint"
                label={t('academics.gradingScale.fields.gradePoint')}
                type="number"
                fullWidth
                value={formData.gradePoint}
                onChange={handleChange}
              />
            </>
          ) : (
            <>
              <FormControl fullWidth margin="dense">
                <InputLabel id="nursery-emoji-level">Emoji Level</InputLabel>
                <Select
                  labelId="nursery-emoji-level"
                  label="Emoji Level"
                  name="emojiValue"
                  value={formData.emojiValue}
                  onChange={handleChange}
                >
                  {nurseryEmojiOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderNurseryEmojiBadge(o, 26)}
                        <span>{o.value} - {o.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                name="description"
                label="Description"
                type="text"
                fullWidth
                value={formData.description}
                onChange={handleChange}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} color="primary" disabled={saving}>
            {saving ? t('common.saving') : currentScale ? t('academics.gradingScale.actions.update') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GradingScalePage;
